/** COLMAP world-to-camera poses. The world is shared with the PLY artifact. */
export interface SolvedCamera { center: number[]; rotation: number[][] }

export function parseCameraPoses(text: string): SolvedCamera[] {
  const cameras: SolvedCamera[] = [];
  let pose = true;
  for (const line of text.split(/\r?\n/)) {
    if (line.trimStart().startsWith("#")) continue;
    if (!pose) { pose = true; continue; }
    if (!line.trim()) continue;
    pose = false;
    const fields = line.trim().split(/\s+/);
    if (fields.length < 10) throw new Error("Kamera pozları okunamadı.");
    const values = fields.slice(1, 8).map(Number);
    if (!values.every(Number.isFinite)) throw new Error("Geçersiz kamera pozu.");
    const norm = Math.hypot(...values.slice(0, 4));
    if (norm < 1e-12) throw new Error("Geçersiz kamera dönüşümü.");
    const [w,x,y,z] = values.slice(0, 4).map(v => v / norm) as [number,number,number,number];
    const r = [[1-2*(y*y+z*z),2*(x*y-z*w),2*(x*z+y*w)],
      [2*(x*y+z*w),1-2*(x*x+z*z),2*(y*z-x*w)],
      [2*(x*z-y*w),2*(y*z+x*w),1-2*(x*x+y*y)]];
    const t = values.slice(4);
    const center = [0,1,2].map(i => -r.reduce((sum,row,j) => sum+row[i]!*t[j]!, 0));
    cameras.push({ center, rotation: r });
  }
  return cameras;
}

/** Schematic camera glyphs; width is a display size, not a claimed focal length. */
export function cameraFrustums(cameras: SolvedCamera[], size: number): Float32Array {
  const vertices: number[] = [];
  for (const { center, rotation: r } of cameras) {
    const corners = [[-1,-.75,1.5],[1,-.75,1.5],[1,.75,1.5],[-1,.75,1.5]]
      .map(v => [0,1,2].map(i => center[i]! + size*r.reduce((sum,row,j) => sum+row[i]!*v[j]!,0)));
    for (let i=0;i<4;i++) {
      vertices.push(...center, ...corners[i]!, ...corners[i]!, ...corners[(i+1)%4]!);
    }
    // Up tick uses -Y in the camera's CV frame and the SAME inverse rotation.
    const up = [0,1,2].map(i => center[i]! - size*r[1]![i]!);
    vertices.push(...center, ...up);
  }
  return new Float32Array(vertices);
}
